import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import { PDFExport } from '@progress/kendo-react-pdf';
import classNames from 'classnames';
import React, { Dispatch, RefObject } from 'react';
import { connect } from 'react-redux';
import { AiFillAppDestination } from 'api/ai-fill/types';
import { DocumentsAPI } from 'api/document/document-core';
import { Document } from 'api/document/types/document';
import { CKCollabEditor } from 'components/organisms/ck-collab-editor';
import { TaskSection } from 'vendor/ckeditor/plugins/document-tasks-section';
import { VariablesUI } from 'vendor/ckeditor/plugins/variables/variables-ui';
import { AiFillMeta } from 'vendor/ckeditor/react-components/ai-fill/types';
import { DocumentHead } from '../document-head';
import styles from './styles.module.css';
import { taskSectionRenderer } from './task-section';

export interface Props {
  singleUserMode?: boolean;
  onEditorReady: (EditorsObject, comments?: any) => void;
  onEditorFocusChange: (editor: Editor) => void;
  emitPrivateSectionStatus: (val: boolean) => void;
  onTitleViewStateChange?: ({
    isVisible,
    docTitle,
  }: {
    isVisible: boolean;
    docTitle: string;
  }) => void;
  onDocumentTitleChange?: (title: string) => void;
  onDocumentLoaded?: (document: Document) => void;
  isPrivateSectionEnabled: boolean;
  distractionFreeMode?: boolean;
  docId: number;
  projectId: number;
  refDocContentWrapper: RefObject<HTMLDivElement>;
  refDocumentWrapper: RefObject<HTMLDivElement>;
  refCollaboratorsListElement: RefObject<HTMLDivElement>;
  dispatch: Dispatch<any>;
  documentTabsFreeMode: boolean;
  maxWidth: boolean;
  pdfExportComponent: React.MutableRefObject<PDFExport | null>;
  readOnly: boolean;
  isResponsive?: boolean;
  isVersionsEditorEnabled?: boolean;
  canUseVersionsEditor?: boolean;
  extraPlugins?: any[];
  extraConfig?: {
    [key: string]: any;
  };
  aiFillMeta?: AiFillMeta;
}

export interface State {
  docTitle: string | null;
  activeEditor: Editor | null;
}

class DocumentEditorBase extends React.PureComponent<Props, State> {
  presenceListElementRef = React.createRef<HTMLDivElement>();

  CKEditorConfig = {
    aiFill: {
      addAsPlaceholderOnly: false,
      destination: AiFillAppDestination.Document,
      // eslint-disable-next-line react/destructuring-assignment
      meta: this.props.aiFillMeta,
    },
    taskSection: {
      renderer: taskSectionRenderer,
    },
  };

  constructor(props) {
    super(props);
    // TODO: Show private section only if there's some content.
    this.state = {
      activeEditor: null,
      docTitle: null,
    };
    this.handleTitleViewChange = this.handleTitleViewChange.bind(this);
    this.onTitleChange = this.onTitleChange.bind(this);
    this.updateTitle = this.updateTitle.bind(this);
    this.onEditorChange = this.onEditorChange.bind(this);
    this.onDocumentTitleEnter = this.onDocumentTitleEnter.bind(this);
  }

  handleTitleViewChange({ isVisible }) {
    const { docTitle } = this.state;
    const { onTitleViewStateChange } = this.props;

    if (onTitleViewStateChange) {
      onTitleViewStateChange({ isVisible, docTitle: docTitle || '' });
    }
  }

  onDocumentTitleEnter() {
    const { activeEditor } = this.state;

    activeEditor?.editing.view.focus();
  }

  onEditorChange(edtr: Editor) {
    this.setState({
      activeEditor: edtr,
    });
    const { onEditorFocusChange } = this.props;

    onEditorFocusChange(edtr);
  }

  onTitleChange(title = '', suppressApi?: boolean) {
    this.setState({ docTitle: title });
    const { docId, projectId, onDocumentTitleChange } = this.props;

    if (!suppressApi) {
      DocumentsAPI.updateDocumentTitle(projectId, docId, title);
    }
    if (onDocumentTitleChange) {
      onDocumentTitleChange(title);
    }
  }

  updateTitle(title: string) {
    this.onTitleChange(title, true);
  }

  render() {
    const {
      isPrivateSectionEnabled,
      refCollaboratorsListElement,
      docId,
      projectId,
      refDocumentWrapper,
      refDocContentWrapper,
      distractionFreeMode,
      onEditorReady,
      onDocumentLoaded,
      emitPrivateSectionStatus,
      singleUserMode,
      documentTabsFreeMode,
      maxWidth,
      pdfExportComponent,
      readOnly,
      isResponsive,
      isVersionsEditorEnabled,
      canUseVersionsEditor,
      extraPlugins,
    } = this.props;
    const { docTitle } = this.state;

    return (
      <div className="ck-editor-custom">
        <div
          className={classNames(styles.container, {
            [styles.container_full_responsive]: isResponsive,
            [styles.container_full]: !isResponsive && distractionFreeMode,
            [styles.tabs_active]: !documentTabsFreeMode,
          })}
        >
          <div
            ref={refDocContentWrapper}
            data-test-id="document.editor"
            data-cy="document.editor"
          >
            <DocumentHead
              refWrapper={refDocumentWrapper}
              onChange={(title: string) => this.onTitleChange(title, true)}
              onEnter={this.onDocumentTitleEnter}
              onTitleViewStateChange={this.handleTitleViewChange}
              className={`document__head ${styles.head} ${styles.ready}`}
              title={docTitle}
              documentMaxWidth={maxWidth}
              readOnly={readOnly}
              isResponsive={isResponsive}
              isVersionsEditorEnabled={isVersionsEditorEnabled && !maxWidth}
            />
            {refCollaboratorsListElement.current ? (
              <CKCollabEditor
                singleUserMode={singleUserMode}
                readOnly={readOnly}
                isResponsive={isResponsive}
                onDocumentLoaded={onDocumentLoaded}
                extraPlugins={[
                  TaskSection,
                  VariablesUI,
                  ...(extraPlugins ?? []),
                ]}
                extraConfig={this.CKEditorConfig}
                presenceListElementRef={refCollaboratorsListElement}
                docId={docId}
                projectId={projectId}
                onEditorReady={onEditorReady}
                maxWidth={maxWidth}
                onDocumentTitleChange={this.updateTitle}
                onEditorFocusChange={this.onEditorChange}
                isPrivateSectionEnabled={isPrivateSectionEnabled}
                emitPrivateSectionStatus={emitPrivateSectionStatus}
                documentTabsFreeMode={documentTabsFreeMode}
                pdfExportComponent={pdfExportComponent}
                title={docTitle}
                isVersionsEditorEnabled={isVersionsEditorEnabled}
                canUseVersionsEditor={canUseVersionsEditor}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

export const DocumentEditor = connect()(DocumentEditorBase);
